import { Grid, Paper, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import RangeSelectorDatetime from '../common/RangeSelectorDatetime'
import Chart from 'react-apexcharts'
import { useGetFilteredStudentsMutation, useGetFilteredSubmissionsMutation } from '../../services/analytics.service'
import moment, { Moment } from 'moment'

const chartStyle = {
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  bgcolor: "#ffffff",
  border: "1px solid #E2E8F0",
  borderRadius: "8px",
  p: 2,
};

function StudentsSubmissionsChart() {
  const [students, setStudents] = useState<any>({});
  const [submissions, setSubmissions] = useState<any>({});
  const [pipeline, setPipeline] = useState({
    ranges: [{
      field: "createdAt",
      start: moment().subtract(1, 'weeks'),
      end: moment()
    }],
    dynamicFilters: [],
    groupOptions: {}
  });
  const [chartData, setChartData] = useState<any>({ categories: [], series: [] });
  const [getStudents] = useGetFilteredStudentsMutation();
  const [getSubmissions] = useGetFilteredSubmissionsMutation();

  const getFilteredStudents = async (pipeline: any) => {
    return await getStudents(pipeline).then((data: any) => {
      const items: any[] = data?.data[0]?.item;
      const count = data?.data[0].count;
      const groupedValues = Object.groupBy(items, ({ createdAt }: any) => createdAt.split('T')[0]);
      const categories = Object.keys(groupedValues).sort((a, b) => new Date(a).valueOf() - new Date(b).valueOf());
      return { categories, groupedValues, count };
    }).catch((err) => console.log(err));
  };

  const getFilteredSubmissions = async (pipeline: any) => {
    return await getSubmissions(pipeline).then((data: any) => {
      const items: any[] = data?.data[0]?.item;
      const count = data?.data[0].count;
      const groupedValues = Object.groupBy(items, ({ createdAt }: any) => createdAt.split('T')[0]);
      const categories = Object.keys(groupedValues).sort((a, b) => new Date(a).valueOf() - new Date(b).valueOf());
      return { categories, groupedValues, count };
    }).catch((err) => console.log(err));
  };

  const handleRangeChage = (range: { start: Moment | null; end: Moment | null }) => {
    setPipeline({
      ...pipeline,
      ranges: [{ field: "createdAt", start: range.start?.toISOString() as any, end: range.end?.toISOString() as any }]
    });
  };

  useEffect(() => {
    const fetch = async () => {
      const [s, sub] = await Promise.all([
        getFilteredStudents(pipeline),
        getFilteredSubmissions(pipeline)
      ]);
      setStudents(s);
      setSubmissions(sub);
    };
    fetch();
  }, [pipeline]);

  useEffect(() => {
    if (students?.categories && submissions?.categories) {
      const merged = [...students.categories, ...submissions.categories]
        .sort((a, b) => new Date(a).valueOf() - new Date(b).valueOf());
      const categories = Array.from(new Set(merged));

      setChartData({
        categories,
        series: [
          {
            name: "Students",
            data: categories.map((item: any) => students.groupedValues[item]?.length || 0)
          },
          {
            name: "Submissions",
            data: categories.map((item: any) => submissions.groupedValues[item]?.length || 0)
          }
        ]
      });
    }
  }, [pipeline, students, submissions]);

  return (
    <Grid item container flex={3} sx={chartStyle}>
      <Grid item container gap={1} sx={{ flexShrink: 0, mb: 1 }} alignItems="center">
        <RangeSelectorDatetime onRangeChange={handleRangeChage} />
        <Paper elevation={0} sx={{ p: 1, border: "1px solid #E2E8F0", borderRadius: "6px" }}>
          <Typography variant="body2" fontWeight={500}>Total Students : {students?.count}</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 1, border: "1px solid #E2E8F0", borderRadius: "6px" }}>
          <Typography variant="body2" fontWeight={500}>Total Submissions : {submissions?.count}</Typography>
        </Paper>
      </Grid>

      <Grid item sx={{ flex: 1, minHeight: 0 }}>
        <Chart
          options={{
            chart: { toolbar: { show: false }, background: "transparent" },
            colors: ["#0F172A", "#059669"],
            xaxis: {
              categories: chartData.categories,
              tickAmount: 8,
              labels: {
                rotate: -45,
                hideOverlappingLabels: true,
                style: { fontSize: "10px", colors: "#64748B" }
              }
            },
            yaxis: { min: 0, forceNiceScale: true, labels: { style: { colors: "#64748B", fontSize: "11px" } } },
            plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } },
            dataLabels: { enabled: false },
            grid: { borderColor: "#E2E8F0" },
            tooltip: { theme: "light" },
            legend: { position: "top" },
          }}
          series={chartData.series}
          type="bar"
          height={300}
        />
      </Grid>
    </Grid>
  );
}

export default StudentsSubmissionsChart;
